import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { MilkProductionReport } from "@/api/types";
import "./ReportsMilkProduction.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** 30 days back from today, UTC — same rolling-window default
 *  `ReportsCropProduction.tsx`'s own `defaultRangeStart` uses, duplicated on purpose
 *  (that file's own reasoning: each report's own default is free to diverge later). */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Milk production by month — Reports' worksheet of qty/amount per (month, unit) across
 * the farm's milk tickets, for a window the owner chooses. ef-053-3, ADR 0009 §6
 * ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsCropProduction.tsx`'s own remarks give, restated here for the identical
 * reason: a production figure read against a window nobody asked for is a number a
 * farmer did not request. The From/To inputs still carry a sensible rolling-30-day
 * default so the form is never two empty fields.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — same reasoning `ReportsCropProduction.tsx`'s own remarks give:
 * `MilkProductionReportEndpoints` resolves the tenant's own single/oldest entity when it
 * is omitted, which is every farm this booth demo models.
 * </p>
 *
 * <p>
 * GAL AND CWT NEVER SUM TOGETHER — a month with both a gal ticket and a cwt ticket
 * renders as TWO rows, never a combined total (`MilkProductionReportRepository`'s own
 * GROUP BY, proven server-side by
 * `Gal_and_cwt_in_the_same_month_stay_on_separate_rows`).
 * </p>
 */
export function ReportsMilkProduction() {
  const fromId = useId();
  const toId = useId();

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);

  const [report, setReport] = useState<MilkProductionReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getMilkProductionReport({ from, to });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the milk production report."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsCropProduction.tsx`'s own identical download gives: paying
   * for the whole CSV body on every date change would be wasted work for the visits that
   * never press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getMilkProductionCsv({ from, to });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsCropProduction.tsx`'s own
      // identical download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-milk-production-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the milk production file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="milk-production">
      <PageHeader
        title="Milk production by month"
        subtitle="Volume and revenue by month, gal and cwt kept separate"
      />

      <a href="/reports" className="milk-production__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="milk-production__panel"
        aria-labelledby="milk-production-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="milk-production-heading" className="milk-production__heading">
          Worksheet
        </h2>

        <form className="milk-production__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="milk-production__field">
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="milk-production__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary milk-production__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="milk-production__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        <Why>
          Rows are grouped by <strong>month</strong>, then <strong>unit</strong> — gal and
          cwt never sum together.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the milk production report."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="No milk tickets were found."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="milk-production__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="milk-production-heading"
            >
              <table className="milk-production__table ruled-rows">
                <caption className="milk-production__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Month</th>
                    <th scope="col">Unit</th>
                    <th scope="col" className="milk-production__num">Qty</th>
                    <th scope="col" className="milk-production__num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.month}-${row.unit}`}>
                      <th scope="row" className="milk-production__month">{row.month}</th>
                      <td>{row.unit}</td>
                      <td className="milk-production__num">{row.qty.toFixed(2)}</td>
                      <td className="milk-production__num milk-production__amount">
                        {formatMoney(row.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="milk-production__export">
              <h3 className="milk-production__export-title">Milk production for your records</h3>
              <p className="milk-production__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary milk-production__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="milk-production__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="milk-production__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
